import { useEffect, useMemo, useState } from 'react';
import cx from 'classnames';
import Table from 'react-bootstrap/Table';
import { ColumnInstance, useSortBy, useTable } from 'react-table';
import { connect, ConnectedProps } from 'react-redux';
import Spinner from 'react-spinkit';
import ResellerSelector from './ResellerSelector';
import PrimaryButton from '../components/PrimaryButton/PrimaryButton';
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { flushLocalStorage, getLocalStorage, setLocalStorage } from '../aaa';
import { getMonthName, getTimeZoneOffsetAsString, isUserHasKey, mod } from '../GlobalFunctions';
import {
  getDateBeginningOfSchoolYear,
  getNextMonthFirstDay,
  getToday,
} from '../AdministratorDashboard/reportUtilities';
import { getResellers, queryMixpanelByGroup } from '../BackendInterface';
import styles from './Reseller.module.scss';
import { UserProfileType } from '../../peekapak-types/DataProtocolTypes';
import { RootState } from '../ApplicationState';

const mapStateToProps = (state: RootState) => ({
  userProfile: state.user.userProfile,
});

const connector = connect(mapStateToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

type Props = PropsFromRedux & {
  userProfile: UserProfileType;
};

enum PresentationMode {
  Chart = 'chart',
  Table = 'table',
}

interface DataPoint {
  label: 'Jan' | 'Feb' | 'Mar' | 'Apr' | 'May' | 'Jun' | 'Jul' | 'Aug' | 'Sep' | 'Oct' | 'Nov' | 'Dec';
  order: number;
  [email: string]: number | string;
}

interface State {
  data: {
    emails: string[];
    dataPoints: DataPoint[];
  };
  dataReady: boolean;
  windowIndex: number;
  errorMessage: Error | null;
  resellersList: string[];
  resellersListReady: boolean;
  selectedReseller: string;
  presentationMode: PresentationMode;
}

const initialState = {
  data: {
    emails: [],
    dataPoints: [],
  },
  dataReady: false,
  windowIndex: 0,
  errorMessage: null,
  resellersList: [],
  resellersListReady: false,
  selectedReseller: '',
  presentationMode: PresentationMode.Table,
};

const ResellerReports = ({ userProfile }: Props) => {
  const [state, setState] = useState<State>(initialState);

  const togglePresentationMode = () => {
    setState((prevState) => ({
      ...prevState,
      presentationMode:
        prevState.presentationMode === PresentationMode.Chart ? PresentationMode.Table : PresentationMode.Chart,
    }));
  };
  //
  // get sellers list
  //
  useEffect(() => {
    let mounted = true;

    if (!state.resellersListReady) {
      getListOfSellers();
    }

    return function cleanup() {
      mounted = false;
    };

    async function getListOfSellers() {
      const storageKey = `${userProfile.email}-resellersList`;
      const list = getLocalStorage(storageKey);

      if (list && mounted) {
        return setState((prevState) => ({
          ...prevState,
          resellersList: list,
          resellersListReady: true,
          selectedReseller: list[0].id,
        }));
      }

      const listFromDb = await getResellers();
      setLocalStorage(storageKey, listFromDb, 6440);

      if (mounted) {
        setState((prevState) => ({
          ...prevState,
          resellersList: listFromDb,
          resellersListReady: true,
          selectedReseller: listFromDb[0].id,
        }));
      }
    }
  }, [userProfile, state.resellersListReady]);

  //
  // get teacher analytics
  //
  useEffect(() => {
    let mounted = true;
    const beginningOfSchoolYear = getDateBeginningOfSchoolYear();
    const today = getToday();

    const cachedData = getLocalStorage(`${state.selectedReseller}-userLogin-${beginningOfSchoolYear}-${today}`);

    if (!cachedData && state.selectedReseller) {
      doQuery();
    } else if (cachedData) {
      if (mounted) {
        setState((prevState) => ({
          ...prevState,
          data: {
            emails: cachedData.emails,
            dataPoints: cachedData.dataPoints,
          },
          dataReady: true,
        }));
      }
    }

    return function cleanup() {
      mounted = false;
    };

    async function doQuery() {
      // console.debug(
      //   `%cGetting teacher login data from `,
      //   'background: blue; color: yellow',
      //   state.selectedSchool
      // );
      interface IDataPoint {
        distinct_id: string;
        email: string;
        time: number;
      }

      try {
        const bOSYresults: Array<IDataPoint> = await queryMixpanelByGroup(
          getScript(state.selectedReseller, beginningOfSchoolYear, today),
          'peekapak',
          '',
        );

        // optimize for storage
        const forStorage = bOSYresults.map((e) => ({
          distinct_id: e.distinct_id,
          email: e.email,
          time: e.time,
        }));

        const buckets = createSchoolYearBuckets();

        const bucketizedData = bucketize(forStorage, buckets);

        const [emails, ordered] = orderBucketizedData(bucketizedData);

        if (mounted) {
          setState((prevState) => ({
            ...prevState,
            data: {
              emails,
              dataPoints: ordered,
            },
            dataReady: true,
          }));
        }

        setLocalStorage(
          `${state.selectedReseller}-userLogin-${beginningOfSchoolYear}-${today}`,
          {
            emails,
            dataPoints: ordered,
          },
          60,
        );
      } catch (error) {
        if (mounted) {
          setState((prevState) => ({
            ...prevState,
            errorMessage: error,
          }));
        }
      }

      interface IChartData {
        label: string;
        order: number;
        [email: string]: number | string;
      }

      function orderBucketizedData(bucketizedData: IBucketizedData): [string[], IChartData[]] {
        const chartData: IChartData[] = [];
        const bucketKeys = Object.keys(bucketizedData);
        const emailsSet = new Set<string>();
        bucketKeys.forEach((month) => {
          const { order, data } = bucketizedData[month];
          const emailsLoginsMap = data.reduce((acc, current) => {
            emailsSet.add(current.email);
            if (!acc[current.email]) {
              acc[current.email] = 0;
            }
            acc[current.email] += 1;
            return acc;
          }, {});

          // we want the 7-index value, Aug to be the first in the sorted list
          // so we use the modulo subtraction function to do that
          chartData.push({
            label: month,
            order: mod(order - 7, 12),
            ...emailsLoginsMap,
          });
        });

        const sorted = chartData.sort((a, b) => a.order - b.order);

        // if a bucket doesn't have one of the emails from
        // the set, then create an entry in the bucket with
        // this email and set login count to 0
        // if not all emails show up in all buckets, then
        // recharts won't work and will just display blank
        sorted.forEach((bucket) => {
          Array.from(emailsSet).forEach((email) => {
            if (!bucket[email]) {
              bucket[email] = 0;
            }
          });
        });

        const sortedEmails = Array.from(emailsSet).sort();

        return [sortedEmails, sorted];
      }

      interface IBucketizedData {
        [label: string]: {
          order: number;
          data: Array<IDataPoint>;
        };
      }

      function bucketize(data: Array<IDataPoint>, buckets: TBuckets) {
        const intermediate: IBucketizedData = {};

        // we initialize intermediate with all buckets so that
        // the end result will have buckets even if the buckets have
        // no data, i.e. 0 occurences so that they can still be graphed
        buckets.forEach((b) => {
          intermediate[b.label] = {
            order: b.order,
            data: [],
          };
        });

        let i = 0;
        while (i < data.length) {
          const datapoint = data[i];

          let j = 0;
          while (j < buckets.length) {
            const bucket = buckets[j];
            if (datapoint.time >= bucket.from && datapoint.time < bucket.to) {
              putIntoIntermediate(datapoint, bucket);
              break;
            }
            j += 1;
          }
          i += 1;
        }

        return intermediate;

        function putIntoIntermediate(d: IDataPoint, b: IBucket) {
          intermediate[b.label].order = b.order;
          intermediate[b.label].data.push(d);
        }
      }

      interface IBucket {
        label: string;
        order: number;
        from: number;
        to: number;
      }

      type TBuckets = Array<IBucket>;

      function createSchoolYearBuckets(): TBuckets {
        const timeZoneOffset = getTimeZoneOffsetAsString();
        let current = new Date(`${beginningOfSchoolYear}T00:00:00.000${timeZoneOffset}`);
        const buckets = [];

        let i = 0;
        while (i < 12) {
          const label = getMonthName(current);
          const from = current.getTime();
          const to = getNextMonthFirstDay(from).getTime();

          buckets.push({
            order: current.getMonth(),
            label,
            from,
            to,
          });

          i += 1;
          current = new Date(to);
        }

        return buckets;
      }
    }

    //
    // JQL script to send to mixpanel
    //
    function main() {
      // @ts-ignore
      const domains = [DOMAINS];
      // @ts-ignore
      const districts = [DISTRICTS];
      return (
        // @ts-ignore
        Events({
          from_date: '%START_DATE%',
          to_date: '%END_DATE%',
        })
          // @ts-ignore
          .filter(function (event) {
            return (
              event.name === 'User Login' &&
              (event.properties['Seller'] === '%SELLER%' ||
                // @ts-ignore
                domains.includes(event.properties['Email Domain']) ||
                districts.includes(event.properties['District']))
            );
          })
          // @ts-ignore
          .map(function (item) {
            const { distinct_id, name, properties, time } = item;

            const { Email } = properties;

            const toReturn = {
              time,
              distinct_id,
              eventName: name,
              email: Email,
            };

            return toReturn;
          })
      );
    }

    //
    // take script and turn it into a string
    // replacing placeholders with real values
    //
    function getScript(seller: string, startDate: string, endDate: string) {
      const functionString = main.toString();
      const domains = (function () {
        switch (seller) {
          //@ts-ignore
          case 'mta':
            return ['eq.edu.au', 'cck29.org', 'olhcmurtoa.catholic.edu.au'];
          //@ts-ignore
          case 'nelsonEducation':
            return ['ddsb.ca', 'tcdsb.org'];
          default:
            return [];
        }
      })();
      const districts = (function () {
        switch (seller) {
          //@ts-ignore
          case 'mta':
            return [];
          //@ts-ignore
          case 'nelsonEducation':
            return ['avonMaitland'];
          default:
            return [];
        }
      })();
      return functionString
        .replace('%START_DATE%', startDate)
        .replace('%END_DATE%', endDate)
        .replace('%SELLER%', seller)
        .replace('DOMAINS', `'${domains.join("','")}'`)
        .replace('DISTRICTS', `'${districts.join("','")}'`);
    }
  }, [state.selectedReseller]);

  //
  // render spinner if data isn't ready yet
  //
  if (!state.errorMessage && (!state.dataReady || !state.resellersListReady)) {
    return (
      <div className={styles.containerSpinner}>
        <Spinner name='line-scale' />
      </div>
    );
  }

  const lineColours = ['#00876c', '#89bf77', '#fff18f', '#f59b56', '#d43d51'];
  const [windowedEmails, windowSpaceSize] = (() => {
    if (!state.dataReady) {
      return [0, 0, 0, 0, []];
    }

    const WINDOW_SIZE = 5;
    const windowSpaceSize = Math.ceil(state.data.emails.length / WINDOW_SIZE);
    const sliceStart = state.windowIndex * WINDOW_SIZE;
    const sliceEnd = Math.min(sliceStart + WINDOW_SIZE, state.data.emails.length);
    return [state.data.emails.slice(sliceStart, sliceEnd), windowSpaceSize];
  })();

  return (
    <div className={styles.container}>
      <div className={styles.controls}>
        <div className={styles.sellerSelectorControl}>
          <ResellerSelector
            isPeekapakAdmin={!!isUserHasKey('KEY_PEEKAPAK_MANAGEMENT')}
            resellersList={state.resellersList}
            onReload={onReload}
            onSelectReseller={onSelectReseller}
            initialValue={state.selectedReseller}
            disableChangeReseller={!isUserHasKey('KEY_RESELLER_REPORTS_SELECT')}
          />
        </div>
        <div className={styles.userSetControl}>
          {state.presentationMode === PresentationMode.Chart && (
            <>
              <PrimaryButton small secondary value='back' onClick={retreatWindow}>
                <i className='fa fa-chevron-left' />
                <i className='fa fa-chevron-left' />
              </PrimaryButton>
              User Set Change
              <PrimaryButton small secondary value='forward' onClick={advanceWindow}>
                <i className='fa fa-chevron-right' />
                <i className='fa fa-chevron-right' />
              </PrimaryButton>
            </>
          )}
          <ChartTableButton presentationMode={state.presentationMode} toggle={togglePresentationMode} />
        </div>
      </div>
      <ContentSwitch parentState={state} windowedEmails={windowedEmails} lineColours={lineColours} />
    </div>
  );

  function advanceWindow() {
    setState((prevState) => ({
      ...prevState,
      windowIndex: mod(state.windowIndex + 1, windowSpaceSize),
    }));
  }

  function retreatWindow() {
    setState((prevState) => ({
      ...prevState,
      windowIndex: mod(state.windowIndex - 1, windowSpaceSize),
    }));
  }

  function onSelectReseller(event) {
    if (state.resellersListReady && state.resellersList.length > 0) {
      const selectedReseller = event.currentTarget.value;
      setState((prevState) => ({
        ...prevState,
        selectedReseller,
        data: [],
        dataReady: false,
      }));
    } else {
      console.warn(`%cDistrict List is Missing`, 'background: black; color:yellow');
    }
  }

  function onReload() {
    flushLocalStorage();
    setState((prevState) => ({
      ...prevState,
      ...initialState,
    }));
  }
};

interface PropsForRender {
  parentState: State;
  windowedEmails: string[];
  lineColours: string[];
}

function ContentSwitch({ parentState, windowedEmails, lineColours }: PropsForRender) {
  if (parentState.errorMessage?.message) {
    return <div className={styles.containerErrorMessage}>{parentState.errorMessage.message}</div>;
  }

  return (
    <>
      {parentState.presentationMode === PresentationMode.Chart ? (
        <Chart parentState={parentState} windowedEmails={windowedEmails} lineColours={lineColours} />
      ) : (
        <ChartAsTable parentState={parentState} />
      )}
    </>
  );
}

function Chart({ parentState, windowedEmails, lineColours }: PropsForRender) {
  if (!parentState.data.emails.length) {
    return (
      <div className={styles.message}>
        <h1>No usage data</h1>
      </div>
    );
  }

  return (
    <ResponsiveContainer id={styles.resellerChart}>
      <LineChart
        width={500}
        height={300}
        data={parentState.data.dataPoints}
        margin={{
          top: 5,
          right: 30,
          left: 20,
          bottom: 5,
        }}
      >
        <CartesianGrid strokeDasharray='3 3' />
        <XAxis dataKey='label' padding={{ left: 30, right: 30 }} />
        <YAxis label={{ value: 'User Logins', angle: -90, position: 'insideLeft' }} />
        <Tooltip />
        <Legend />
        {windowedEmails.map((email, index) => (
          <Line dataKey={email} stroke={lineColours[index % 8]} key={email} activeDot={{ r: 6 }} />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
}

function ChartTableButton({ presentationMode, toggle }: { presentationMode: PresentationMode; toggle: () => void }) {
  const title = presentationMode === PresentationMode.Chart ? 'Table' : 'Chart';
  return (
    <PrimaryButton small value='chartTableButton' onClick={toggle}>
      {title}
    </PrimaryButton>
  );
}

function ChartAsTable({ parentState }: { parentState: State }) {
  const data = useMemo(() => {
    return parentState.data.emails.map((e) => {
      const cols: Record<string, number> = {};
      parentState.data.dataPoints.forEach((month) => {
        cols[month.label] = month[e] as number;
      });
      return {
        user: e,
        ...cols,
      };
    });
  }, [parentState]);

  const columns = useMemo(() => {
    const cols = [];
    cols[0] = {
      Header: 'User',
      accessor: 'user' as const,
    };

    parentState.data.dataPoints.forEach((month) => {
      cols.push({
        Header: month.label,
        accessor: month.label,
      });
    });

    return cols;
  }, [parentState]);

  const tableInstance = useTable({ columns, data }, useSortBy);
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = tableInstance;

  if (!parentState.data.emails.length) {
    return (
      <div className={styles.message}>
        <h1>No usage data</h1>
      </div>
    );
  }

  return (
    <div className={styles.table}>
      <h1 className={styles.title}>User Logins</h1>
      <Table {...getTableProps()}>
        <thead>
          {headerGroups.map((headerGroup) => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => (
                <th
                  {...column.getHeaderProps(column.getSortByToggleProps())}
                  className={cx(styles.thCentered, styles.tableHeading)}
                >
                  {column.render(TableHeader)}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {rows.map((row) => {
            prepareRow(row);
            return (
              <tr {...row.getRowProps()}>
                {row.cells.map((cell, i) => {
                  return (
                    <td {...cell.getCellProps()} className={cx({ [`${styles.tdCentered}`]: i > 0 })}>
                      {cell.value ? cell.render('Cell') : null}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </Table>
    </div>
  );
}

function TableHeader({ column }: { column: ColumnInstance }) {
  return (
    <span className={styles.headerContent}>
      {column.Header}
      <span className={styles.sortIndicator}>
        {column.isSorted &&
          (column.isSortedDesc ? <i className='fa fa-chevron-down' /> : <i className='fa fa-chevron-up' />)}
      </span>
    </span>
  );
}

export default connector(ResellerReports);
